import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomTextarea from '../../../components/common/CustomTextarea'
import CustomSelect from '../../../components/common/CustomSelect'
import CustomCheckBox from '../../../components/common/CustomCheckBox'
import * as Yup from 'yup'

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  description: Yup.string().required('Description is required'),
  priority: Yup.string().trim().required('Priority is required'),
  dueDate: Yup.string().required('Due Date is required'),
  assignTo: Yup.string().required('Assign To is required'),
  company: Yup.string().trim().required('Company is required'),
   bigButton: Yup.boolean(),
  bigTaskType: Yup.string()
    .nullable()
    .when('bigButton', {
      is: true,
      then: (schema) => schema.required('Please select a Big Task Type'),
      otherwise: (schema) =>
        schema.test('is-null', 'Please unselect Big Task Type', (value) => value === null),
    }),

  bigTaskStatus: Yup.string()
    .nullable()
    .when('bigButton', {
      is: true,
      then: (schema) => schema.required('Please select a Big Task Status'),
      otherwise: (schema) =>
        schema.test('is-null', 'Please unselect Big Task Status', (value) => value === null),
    }),
})

function Edit() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [TeamOptions, setTeamOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [bigTaskTypeOptions, setBigTaskTypeOptions] = useState([])
  const [bigTaskStatusOptions, setBigTaskStatusOptions] = useState([])
const location = useLocation()

const from = location.state?.from || '/task' 
  const formik = useFormik({
    onSubmit: (values) => handleSubmit(values),
    validationSchema,
    initialValues: {
      name: '',
      description: '',
      priority: '',
      assignTo: '',
      dueDate: '',
      company: '',
      paidButton: false,
      bigButton: false,
      printButton: false,
      bigTaskType: null,
      bigTaskStatus: null,
    },
  })

  const fetchDropdowns = async () => {
    try {
      const [teamRes, companyRes, bigTypeRes, bigStatusRes] = await Promise.all([
        axiosAuthInstance.get('team/dropdown'),
        axiosAuthInstance.get('company/dropdown'),
        axiosAuthInstance.get('big-task-type/dropdown'),
        axiosAuthInstance.get('big-task-status/dropdown')
      ])

      setTeamOptions(teamRes.data.result.map(item => ({
        value: item._id,
        label: `${item.full_name} (${item.role})`
      })))

      setCompanyOptions(companyRes.data.result.map(item => ({
        value: item._id,
        label: item.name
      })))

      setBigTaskTypeOptions(bigTypeRes.data.result.map(item => ({
        value: item._id,
        label: item.name
      })))

      setBigTaskStatusOptions(bigStatusRes.data.result.map(item => ({
        value: item._id,
        label: item.name
      })))

    } catch (err) {
      console.error('Error fetching dropdowns:', err)
    }
  }

  const fetchData = async () => {
    try {
      const response = await axiosAuthInstance.get(`task/detail/${id}`)
      const task = response.data.result

      formik.setValues({
        name: task.name || '',
        description: task.description || '',
        priority: task.priority || '',
        dueDate: task.dueDate?.split('T')[0] || '',
        assignTo: task.assignTo?._id || '',
        company: task.company?._id || '',
        paidButton: task.paidButton || false,
        bigButton: task.bigButton || false,
        printButton: task.printButton || false,
        bigTaskType: task.bigTaskType?._id || null,
        bigTaskStatus: task.bigTaskStatus?._id|| null
      })
      setLoading(false)
    } catch (error) {
      console.log('error in fetching edit data: ', error)
    }
  }

  useEffect(() => {
    fetchDropdowns()
    fetchData()
  }, [id])

  const handleSubmit = async (values) => {
    setSubmitted(true)
    try {
      await axiosAuthInstance.put(`task/update/${id}`, values)
      navigate(from)
    } catch (error) {
      console.error('Error updating task', error)
    } finally {
      setSubmitted(false)
    }
  }

  const PriorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Emergency', label: 'Emergency' },
  ]

  if (loading) return <div>Loading...</div>

  return (
    <FormikProvider value={formik}>
      <form
        className='flex flex-col p-4 gap-4'
        onSubmit={(e) => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Field name='name' label='Task Name' placeholder='Task Name' required component={CustomInput} />
        <Field name='description' label='Description' placeholder='Description' required component={CustomTextarea} />
        <Field name='company' label='Company' placeholder='Company' required component={CustomSelect} options={companyOptions} />
        <Field name='priority' label='Priority' placeholder='Priority' required component={CustomSelect} options={PriorityOptions} />
        <Field name='dueDate' label='Due Date' placeholder='Due Date' required type='date' component={CustomInput} />
        <Field name='assignTo' label='Assign To' placeholder='Assign To' required component={CustomSelect} options={TeamOptions} />

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <Field name='paidButton' label='Paid Task' component={CustomCheckBox} />
          <Field
            name='bigButton'
            label='Big Task'
            component={CustomCheckBox}
            onChange={(e) => {
              const isChecked = e.target.checked
              formik.setFieldValue('bigButton', isChecked)
              if (!isChecked) {
                formik.setFieldValue('bigTaskType', null)
                formik.setFieldValue('bigTaskStatus', null)
                 // Mark fields as touched so errors appear
      formik.setFieldTouched('bigTaskType', true)
      formik.setFieldTouched('bigTaskStatus', true)
              }
            }}
          />
          <Field name='printButton' label='Printing Task' component={CustomCheckBox} />
        </div>

        {formik.values.bigButton && (
          <div className='bg-white border border-gray-300 rounded-lg p-4 mt-4 shadow'>
            <h2 className='text-lg font-semibold mb-4'>Big Task Details</h2>
            <Field
              name='bigTaskType'
              label='Big Task Type'
              component={CustomSelect}
              options={bigTaskTypeOptions}
              required
            />
            <Field
              name='bigTaskStatus'
              label='Big Task Status'
              component={CustomSelect}
              options={bigTaskStatusOptions}
              required
            />
          </div>
        )}

        <button
          type='submit'
          disabled={submitted}
          className='bg-primary text-xl font-medium text-white rounded-lg px-6 py-2'
        >
          {submitted ? (
            <div className='w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent'></div>
          ) : (
            'Update'
          )}
        </button>
      </form>
    </FormikProvider>
  )
}

export default Edit